/**
 * Order WA Pending Count — singleton service.
 *
 * Polls /api/ai/order-wa/pending-count every 30s (only when tab is visible)
 * and notifies subscribed React components when the count changes.
 *
 * Only active when localStorage.orderWaInbox === 'true'.
 *
 * Usage:
 *   orderWaPendingCount.subscribe(setter)  → returns unsubscribe fn
 *   orderWaPendingCount.refresh()          → force immediate fetch
 *   orderWaPendingCount.current            → last known count
 */

import axios from 'axios';

const AI_PATH    = process.env.REACT_APP_AI_PATH;
const POLL_MS    = 30 * 1000;
const TIMEOUT_MS = 10000;

let current       = 0;
const subscribers = new Set();
let timer         = null;
let inFlight      = null;

const hasAccess = () => {
  try { return localStorage.getItem('orderWaInbox') === 'true'; } catch { return false; }
};

async function fetchNow() {
  if (!hasAccess() || !AI_PATH) return;
  if (inFlight) return inFlight;
  inFlight = axios.get(`${AI_PATH}/api/ai/order-wa/pending-count`, { timeout: TIMEOUT_MS })
    .then((res) => {
      const next = Number(res.data?.count ?? 0);
      if (next !== current) {
        current = next;
        for (const set of subscribers) set(current);
      }
    })
    .catch((err) => { console.warn('[OrderWaPendingCount] fetch failed:', err.message); })
    .finally(() => { inFlight = null; });
  return inFlight;
}

function startPoll() {
  if (timer) return;
  timer = setInterval(() => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    fetchNow();
  }, POLL_MS);
}

function stopPoll() {
  if (!timer) return;
  clearInterval(timer);
  timer = null;
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') fetchNow();
  });
}

const orderWaPendingCount = {
  subscribe(setter) {
    subscribers.add(setter);
    setter(current);
    if (subscribers.size === 1) {
      startPoll();
      fetchNow();
    }
    return () => {
      subscribers.delete(setter);
      if (subscribers.size === 0) stopPoll();
    };
  },
  refresh: fetchNow,
  get current() { return current; },
};

export default orderWaPendingCount;
